/**
 * Arbeitsbereich in der Kopfzeile (Design 4.4, Variante B): Brotkrumen-Pfad ohne Wappen,
 * z. B. „SmartRegionAUF › VG Fuchstal › Unterdießen ▾“. Jedes Glied wechselt dorthin, das letzte
 * klappt Nachbarn und Unterpunkte auf. Darunter das Band „Du arbeitest in …“.
 * Ohne Gliederung (Einzelkunde) rendern beide nichts.
 */
import { ChevronDown, ChevronRight } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { childrenOf, pathTo, shortName, useOrganisations } from '@/lib/organisations.js';
import { setWorkspace, useWorkspace } from '@/lib/workspace.js';

export { shortName };

export function WorkspacePath(): JSX.Element | null {
  const orgs = useOrganisations();
  const ws = useWorkspace();
  if (!ws) return null;
  const path = pathTo(orgs.items, ws.node.id);
  const parent = ws.node.parent_id;
  const siblings = parent ? childrenOf(orgs.items, parent).filter((o) => o.id !== ws.node.id) : [];
  const children = childrenOf(orgs.items, ws.node.id);

  return (
    <nav aria-label="Arbeitsbereich" className="flex min-w-0 items-center gap-1 text-sm">
      {path.slice(0, -1).map((o) => (
        <span key={o.id} className="flex min-w-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setWorkspace(o.id)}
            className="truncate text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {shortName(o)}
          </button>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
        </span>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 rounded-md px-1.5 py-1 font-semibold text-foreground hover:bg-muted" aria-label={`Arbeitsbereich ${ws.node.name} wechseln`}>
          <span className="truncate">{shortName(ws.node)}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {children.length > 0 ? (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Darunter</DropdownMenuLabel>
              {children.map((o) => (
                <DropdownMenuItem key={o.id} onSelect={() => setWorkspace(o.id)}>
                  {o.name}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
          {children.length > 0 && siblings.length > 0 ? <DropdownMenuSeparator /> : null}
          {siblings.length > 0 ? (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Daneben</DropdownMenuLabel>
              {siblings.map((o) => (
                <DropdownMenuItem key={o.id} onSelect={() => setWorkspace(o.id)}>
                  {o.name}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
          {children.length === 0 && siblings.length === 0 ? (
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Keine weiteren Einträge</DropdownMenuLabel>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}

/** Band unter der Kopfzeile in der Hausfarbe: wo man arbeitet und wem Neues gehoert. */
export function WorkspaceBand(): JSX.Element | null {
  const ws = useWorkspace();
  if (!ws) return null;
  const name = shortName(ws.node);
  return (
    <div role="status" className="bg-brand-500 px-4 py-1.5 text-sm text-white md:px-6">
      Du arbeitest in: <strong className="font-semibold">{name}</strong>
      {' · '}
      {ws.canWrite ? `Neue Daten gehören ${name}` : 'Hier darfst du nur lesen'}
    </div>
  );
}
